<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2022 (original work) Open Assessment Technologies SA ;
    import { onMount, afterUpdate, createEventDispatcher } from 'svelte';
    import { __, generateElementId, getActualKey, normalizeOptions, visibilityObserver } from '@oat-sa-private/ui-core';
    import { allFeedbacks } from '../feedback/index.js';
    import Icon from '../icon/Icon.svelte';

    const dispatch = createEventDispatcher();

    /**
     * @typedef {Object} Option
     * @property {string} key
     * @property {string} label
     */

    /**
     * @typedef {Object} FeedbackProps
     * @property {boolean} showValidFeedback
     * @property {boolean} showInvalidFeedback
     */

    /**
     * @typedef {Object} CustomValidity
     * @property {string} msg
     * @property {boolean} valid
     */

    /**
     * Component renders dropdown with optional feedback
     * @property {string} [value=''] - The value (the key of the selected option)
     * @property {Array[Option]|Object} [options=[]] - An array of Options or Object with structure: {`key1`: `label1`, `key2`: `label2`, ...}
     * @property {string} [placeholder=''] - The placeholder label to display when the field doesn't have a selected value
     * @property {boolean} [reset=true] - Add an option to remove the current value
     * @property {string} [resetLabel='leave blank'] - The label of the option that resets the current value
     * @property {boolean} [disabled=false] - Corresponds to native HTML `disabled` attribute
     * @property {boolean} [required=false] - Corresponds to native HTML `required` attribute
     * @property {('small'|'medium'|'large')} [height='medium'] - height of input
     * @property {number} [visibleOptions=4] - The maximum number of list options to show without needing to scroll the list
     * @property {number} size - width defined as a count of visible letters
     * @property {boolean} [fullwidth=false] - Should the element display as block-level and fill its parent?
     * @property {string} feedback - Selects a feedback subcomponent from an internal list
     * @property {FeedbackProps} [feedbackProps={}] - Configuration properties for the integrated feedback component
     * @property {string} feedbackLang - language code for feedbacks
     * @property {Object|CustomValidity} [customValidity={valid: true}] - Validity object for invalidating an otherwise valid Dropdown from the outside.
     * @property {boolean} [selectFocused=true] -
     * @property {boolean} [lite=false]
     * @fires validation {CustomEvent} fired when validation has performed with validity status in details
     * @fires change {CustomEvent} value change event, details contains new value
     */

    export let value = '';
    export let options = [];
    export let fullwidth = false;
    export let height = 'medium';
    export let disabled = false;
    export let placeholder = '';
    export let reset = true;
    export let resetLabel = __('leave blank');
    export let size;
    export let visibleOptions = 4;
    if (visibleOptions === 0) {
        // a value of 0 is the intended way to disable visibleOptions limitation (expand list as tall as possible)
        visibleOptions = 999;
    }
    // for feedback:
    export let required = false;
    export let feedback;
    export let feedbackProps = {};
    export let feedbackLang;
    export let customValidity = { valid: true };
    export let selectFocused = true;
    export let lite = false;

    const heights = ['small', 'medium', 'large'];
    if (!heights.includes(height)) {
        height = 'medium';
    }

    // DOM elements
    let outerElement;
    let buttonElement;
    let listElement;

    const resetIndex = reset ? 0 : -1;

    // State
    let optionsArray = getOptionsArray(options);
    let index = getOptionIndex(value); // index of current active(selected) option
    let fixedSizeLetters;
    let numberOfOptions;
    let hasSelection = false;
    let selectedLabel = null;
    let firstTry = true; // false after first blur or opening
    let focus = false;
    let focusedValue = value; // value of currently focused element
    let focusedIndex = index; // index of currently focused element
    let keyPressed = false; // mark that user work by keyboard
    let spaceAbove = 0; // distance in pixels from top of outerElement to top of viewport
    let lastDropdownOpenInfo;

    let isWithRubyClass = false;
    $: {
        optionsArray.forEach(option => {
            if (/<ruby>/.test(option.label)) {
                isWithRubyClass = true;
            }
        });
    }

    $: optionsArray = getOptionsArray(options);
    $: numberOfOptions = optionsArray.length;
    $: fixedSizeLetters = size ? parseInt(size, 10) : null;

    $: {
        focusedIndex = getOptionIndex(focusedValue);
        index = getOptionIndex(value);
        hasSelection = index > resetIndex;
        selectedLabel = hasSelection ? optionsArray[index].label : placeholder;
        if (lite) {
            height = 'small';
        }
    }

    let opened = false;
    let dropDirection = 'down';
    let openedListWidth;

    // For list inner scroll shadows:
    let fullyScrolledUp = true;
    let fullyScrolledDown = true;

    // for afterUpdate
    let listHasFocus = false;
    let lastIndex = -1;

    let validity = {};

    let customCSSProperties;
    $: {
        customCSSProperties = `--visible-options: ${visibleOptions}; --space-above: ${spaceAbove}px;`;
        if (fixedSizeLetters) {
            customCSSProperties += `--fixed-size-letters: ${fixedSizeLetters};`;
        }
        if (openedListWidth) {
            customCSSProperties += `--opened-list-width: ${openedListWidth}px;`;
        }
    }

    /**
     * Validate the dropdown element against feedback props, and set local validity
     * @param {Boolean} [emit=true] - emit a validation event or not
     * @fires validation
     */
    function validate(emit = true) {
        // Start in valid state
        validity = {
            messages: [],
            overallValidity: true
        };

        if (required) {
            validity.messages = [
                {
                    msg: __('Answer required'),
                    valid: hasSelection
                }
            ];
            validity.overallValidity = hasSelection;
        }

        // Only apply customValidity if defined and no other invalid rules
        if (customValidity && !customValidity.valid && validity.overallValidity) {
            validity.messages = [customValidity];
            validity.overallValidity = customValidity.valid;
        }

        if (emit) {
            dispatch('validation', validity);
        }
    }

    /**
     * Create dropdown options array
     * @param {Array} optionProps
     * @returns {Array}
     */
    function getOptionsArray(optionProps) {
        const array = normalizeOptions(optionProps);

        if (reset) {
            array.unshift({ key: null, label: resetLabel, id: generateElementId('option') });
        }

        return array;
    }

    /**
     * Keypress handler
     * @param {KeyboardEvent} e
     */
    function handleKeyDown(e) {
        const key = getActualKey(e);
        const step = selectFocused || opened ? 1 : 0;
        switch (key) {
            case 'enter':
            case 'space':
                if (opened) {
                    selectOption(focusedIndex, e);
                    close();
                    focusButton();
                } else {
                    open(e);
                }
                e.preventDefault();
                break;

            case 'esc':
                close();
                focusButton();
                break;

            case 'tab':
                close();
                break;

            case 'down':
                open(e);
                setFocusedOption(focusedIndex + step, e);
                e.preventDefault();
                break;

            case 'up':
                open(e);
                setFocusedOption(focusedIndex - step, e);
                e.preventDefault();
                break;

            case 'home':
                open(e);
                setFocusedOption(0, e);
                e.preventDefault();
                break;

            case 'end':
                open(e);
                setFocusedOption(numberOfOptions - 1, e);
                e.preventDefault();
                break;

            default:
                // whitelist 0-9, a-z for list navigation
                if (key >= '0' && key <= 'z') {
                    const newIndex = findMatchingOptionIndex(key);
                    setFocusedOption(newIndex, e);
                    e.preventDefault();
                }
        }
        keyPressed = true;
    }

    /**
     * Handle click on a list element
     * @param {Number} selectedIndex
     * @param {Event} e
     */
    function handleClick(selectedIndex, e) {
        if (
            e.type === 'mouseup' &&
            lastDropdownOpenInfo &&
            Date.now() - lastDropdownOpenInfo.timestamp < 600 &&
            lastDropdownOpenInfo.buttonTop !== Math.trunc(buttonElement.getBoundingClientRect().top)
        ) {
            //if dropdown is at the bottom of nested scroll container, and still opens to lower hemispehere,
            //  then browser may autoscroll container to fit option-list once it becomes visible:
            //  so when user does dropdown-button click, mousedown happens on button, but this click's mouseup is already on option-list.
            lastDropdownOpenInfo = null;
            return;
        }

        selectOption(selectedIndex, e);
        close();
        focusButton();
        keyPressed = false;
        lastDropdownOpenInfo = null;
    }

    /**
     * Gets the option index given the value.
     * @param {String} key - The value to get the index for
     * @returns {Number} - The index of the value if it exists, or the `resetIndex`
     */
    function getOptionIndex(key) {
        if (key) {
            return optionsArray.findIndex(option => option.key === key);
        }
        return resetIndex;
    }

    /**
     * Gets the option value given the index.
     * @param {Number} idx - The index to get the value for
     * @returns {String} - The value at the given index, or an empty string if it does not exist
     */
    function getOptionValue(idx) {
        const selectedOptionKey = optionsArray[idx].key;
        return selectedOptionKey === null ? '' : selectedOptionKey;
    }

    /**
     * Finds the index of the option matching a given keyboard input, which should be focused next
     * @param {String} typing - only a single letter
     * @returns {Number} index
     */
    function findMatchingOptionIndex(typing) {
        const doesMatchTyping = option => option.label.toLowerCase().startsWith(typing);

        // Simple 1-keypress navigation for the options list
        // - If the user types 'a' repeatedly, we will loop through all options beginning with 'a'
        // - If he types a character which doesn't start any option label, nothing happens
        // - The reset option will not be selectable by first character
        const realOptions = reset ? optionsArray.slice(1) : optionsArray;
        const matches = realOptions.filter(doesMatchTyping);
        if (matches.length) {
            const currentIndexInMatches = matches.indexOf(optionsArray[focusedIndex]);
            const nextIndexInMatches = (currentIndexInMatches + 1) % matches.length;
            const nextMatch = matches[nextIndexInMatches];
            return getOptionIndex(nextMatch.key);
        }
        return -1;
    }

    /**
     * Sets the focused option
     * @param {Number} selectedIndex
     * @param {Event} e
     */
    function setFocusedOption(selectedIndex, e) {
        if (selectedIndex < 0 || selectedIndex >= numberOfOptions) {
            return;
        }
        if (selectFocused) {
            // the focused option is always selected
            selectOption(selectedIndex, e);
        } else {
            // the focus is navigable, we only update the focused position
            focusedValue = getOptionValue(selectedIndex);
        }
    }

    /**
     * Handler to respond to selection of a list option
     * Sets the component's internal list index pointer
     * @param {Number} selectedIndex
     * @param {Event} event
     * @fires change
     */
    function selectOption(selectedIndex, event) {
        if (selectedIndex < 0 || selectedIndex >= numberOfOptions) {
            return;
        }

        index = selectedIndex;
        value = getOptionValue(selectedIndex);
        focusedValue = value;
        hasSelection = index > resetIndex;

        dispatch('change', { value, event });

        validate();
    }

    /**
     * Handler to respond to Button click
     * Toggles the Dropdown
     * @param {Event} e
     */
    function toggle(e) {
        if (opened) {
            close();
            focusButton();
        } else {
            open(e);
        }
        keyPressed = false;
    }

    /**
     * Opens the Dropdown, if permitted
     * @param {Event} e
     */
    function open(e) {
        if (!opened && !disabled) {
            dropDirection = detectLowerHemisphere() ? 'up' : 'down';

            const box = outerElement?.getBoundingClientRect() || {};
            openedListWidth = box.width;
            //when closed, listbox-wrapper has relative position and defines button width (stretching it to fit the longest option)
            //when opened, listbox-wrapper has absolute position and no longer has an effect on button width,
            //so we set fixed button width with var(--opened-list-width)

            opened = true;
            firstTry = false;
            if (focusedIndex !== index) {
                setFocusedOption(index, e);
            }
            if (e.type === 'mousedown') {
                lastDropdownOpenInfo = {
                    timestamp: Date.now(),
                    buttonTop: Math.trunc(buttonElement.getBoundingClientRect().top)
                };
            }
        }
    }

    /**
     * Closes the Dropdown
     */
    function close() {
        if (opened) {
            opened = false;
            openedListWidth = null;
            listHasFocus = false;
            validate();
        }
    }

    /**
     * Focuses the Button
     */
    function focusButton() {
        if (buttonElement) {
            buttonElement.focus();
        }
    }

    /**
     * Tells if the Dropdown is positioned in the bottom half of the viewport
     * @returns {Boolean} true if lower half
     */
    function detectLowerHemisphere() {
        if (!outerElement) {
            return false;
        }
        const midpoint = document.body.clientHeight / 2;
        const box = outerElement.getBoundingClientRect();
        spaceAbove = box.top;
        const dropdownMidY = (box.top + box.bottom) / 2;
        return dropdownMidY > midpoint;
    }

    function cleanTags(label) {
        const div = document.createElement('div');
        div.innerHTML = label;
        return div.textContent || div.innerText || '';
    }

    $: selectedLabelWithoutTags = cleanTags(selectedLabel);

    /**
     * Svelte lifecycle method
     */
    onMount(() => {
        // compute initial validity but only inform child (Feedback) for now, not parent
        validate(false);
    });

    /**
     * Svelte lifecycle method
     */
    afterUpdate(() => {
        if (listElement && opened && !listHasFocus) {
            // set focus on list after open
            listHasFocus = true;
            listElement.focus({ preventScroll: true });
        }

        const activeOptionElement = focusedIndex > -1 ? document.getElementById(optionsArray[focusedIndex].id) : null;

        // scroll to activeOption
        if (
            activeOptionElement &&
            listElement &&
            listElement.scrollHeight > listElement.clientHeight &&
            focusedIndex !== lastIndex
        ) {
            const scrollBottom = listElement.clientHeight + listElement.scrollTop;
            const elementBottom = activeOptionElement.offsetTop + activeOptionElement.offsetHeight;
            if (elementBottom > scrollBottom) {
                listElement.scrollTop = elementBottom - listElement.clientHeight;
            } else if (activeOptionElement.offsetTop < listElement.scrollTop) {
                listElement.scrollTop = activeOptionElement.offsetTop;
            }
            lastIndex = focusedIndex;
        }
    });
</script>

<style>
    .select {
        /* local vars */
        --height-small: calc(2rem + 1em);
        --height-medium: calc(3.5rem + 1em);
        --height-large: calc(6rem + 1em);
        --button-height: var(--height-medium);
        /**
         * Calculations for list element max-height:
         * - based on the height of a single-line option (not wrapping)
         * - it should show up to --visible-options amount of items; can also be set high enough to appear unlimited
         * - it should not overflow viewport (can be controlled from outside by setting --dropdown-total-max-height)
         * - button should remain visible when opened
         * - expands-up list should be restricted to 50% of --total-max-height to avoid overflowing at start of container
         * - expands-down list not restricted because it can use extra space at end of container
         */
        --visible-options: 4; /* prop will override this default */
        --visible-options-height: calc(var(--visible-options) * var(--button-height));
        --total-max-height: var(
            --dropdown-total-max-height,
            80vh
        ); /* fallback value if --dropdown-total-max-height not set outside */
        --viewport-offset-top: var(
            --dropdown-viewport-offset-top,
            10vh
        ); /* fallback value if --dropdown-viewport-offset-top not set outside */
        --list-margin: 1rem;
        --list-expands-up-max-height: calc(var(--space-above) - var(--viewport-offset-top) - var(--list-margin));
        --list-expands-down-max-height: calc(
            var(--total-max-height) - var(--list-expands-up-max-height) - var(--button-height) - 2 * var(--list-margin)
        ); /* using largest button height to have more margin for error across all sizes */

        --button-padding-inline-start: 2rem;
        --button-padding-inline-end: 6rem;
        --button-icon-width: 2rem;
        --option-padding-inline-start: 2rem;

        display: inline-block;
        vertical-align: middle;
        position: relative;
        height: var(--button-height);
        max-width: min(calc(100% - 2rem), 72rem);
        font-size: var(--fontsize-body);
        font-weight: normal;

        &.height-small {
            --button-height: var(--height-small);
            --button-padding-inline-start: 1.5rem;
        }
        &.height-large {
            --button-height: var(--height-large);
            --button-padding-inline-start: 2.5rem;
        }
        &.fullwidth {
            min-width: 100%;
            max-width: 100%;
        }
        &.fixed-size {
            @add-mixin width-to-fit-letters var(--fixed-size-letters);
            --extra-size: calc(
                var(--button-padding-inline-start) + var(--button-padding-inline-end) + 2 * var(--border-thin) + 1.25rem
            );
            width: calc(var(--width-to-fit-letters) + var(--extra-size));
            min-width: calc(var(--extra-size)+ var(--button-icon-width));
        }

        & button {
            display: block;
            position: relative;
            font-family: inherit;
            font-size: var(--fontsize-body);
            font-weight: normal;
            text-align: start;
            white-space: nowrap;
            height: 100%;
            width: 100%;
            padding-block: 0;
            padding-inline-start: var(--button-padding-inline-start);
            padding-inline-end: var(--button-padding-inline-end);
            margin-bottom: 0;
            color: var(--color-text-default);
            background: var(--color-bg-input-default);
            border: var(--border-thin) solid var(--color-border-input-default);
            cursor: pointer;

            letter-spacing: inherit;
            word-spacing: inherit;

            &:hover,
            &:focus,
            &:active {
                outline: none;
                border-color: var(--color-border-actionable-hover);
            }

            &:disabled,
            &:disabled:hover,
            &:disabled:focus {
                color: var(--color-text-disabled);
                border-color: var(--color-border-disabled);
                background: var(--color-bg-disabled-subtle);
            }

            & span {
                display: block;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                width: 100%;
                position: relative;
                color: var(--color-text-default);

                &.dropdown-placeholder {
                    color: var(--color-text-disabled);
                }
            }
            &.with-ruby {
                height: 120%;
            }

            /* down arrow */
            & :global(svg) {
                position: absolute;
                inset-inline-end: 2rem;
                inset-inline-start: unset;
                top: calc(50% - 1rem);
            }
        }

        & :global(button[type='button']:focus-visible) {
            @add-mixin simple-outline;
        }

        & :global-nested(.option) {
            @add-mixin outline-focus 0;

            &.focus-visible {
                @add-mixin simple-outline var(--color-border-focus), -0.25rem;
                background: var(--color-bg-disabled-subtle);
            }
        }

        &.opened button {
            background: var(--color-bg-actionable-subtle-hover);
            border-color: var(--color-border-actionable-hover);
        }

        /* openable list part */
        /* the wrapper is the target for show/hide behaviour, and also has the scroll shadows */
        & .listbox-wrapper {
            display: block;
            position: relative;
            height: 0;
            overflow: hidden;
            top: 0;
            z-index: var(--layer-2);

            /* scroll shadows */
            &.has-top-shadow::before {
                content: '';
                position: absolute;
                width: 100%;
                height: 50%;
                top: 0;
                left: 0;
                @add-mixin shadow-top-inset;
                pointer-events: none;
                z-index: var(--layer-2);
            }
            &.has-bottom-shadow::after {
                content: '';
                position: absolute;
                width: 100%;
                height: 50%;
                bottom: 0;
                left: 0;
                @add-mixin shadow-bottom-inset;
                pointer-events: none;
                z-index: var(--layer-2);
            }
        }

        /* the listbox represents the <ul>, is scrollable and its height is controlled */
        & .listbox {
            display: block;
            overflow-y: hidden;
            overflow-x: hidden;
            background: var(--color-bg-input-default);
            border: var(--border-thin) solid var(--color-border-input-default);
            min-height: calc(2 * var(--button-height));
            max-height: min(var(--visible-options-height), var(--list-expands-down-max-height));

            &:focus {
                outline: none;
            }
        }

        & .option {
            display: block;
            min-height: var(--button-height);
            padding-block: 1.5rem;
            /* to keep width, should have same total paddings as `button` */
            /* can substract scrollbar-width (~2rem) */
            padding-inline-start: var(--option-padding-inline-start);
            padding-inline-end: calc(
                var(--button-padding-inline-end) + var(--button-padding-inline-start) -
                    var(--option-padding-inline-start)
            );
            cursor: pointer;

            &:hover {
                background: var(--color-bg-input-hover);
            }
            &.selected {
                background: var(--color-bg-actionable-hover);
                color: var(--color-text-actionable);
            }
            &.blank {
                font-style: italic;
            }
        }

        &.opened {
            width: var(--opened-list-width);

            & .listbox-wrapper {
                height: auto;
                position: absolute;
                top: calc(-1 * var(--border-thin) + var(--button-height));
                width: 100%;
            }

            & .listbox {
                overflow-y: auto;
            }
        }

        &.expands-up.opened {
            & .listbox-wrapper {
                top: auto;
                bottom: calc(100% - var(--border-thin));
            }

            & .listbox {
                max-height: min(var(--visible-options-height), var(--list-expands-up-max-height));
            }
        }

        &.height-small .option {
            padding-block: 0.5rem;
        }
        &.height-large .option {
            padding-block: 2.5rem;
        }

        &.required:not(.opened) .option.blank {
            /* plus 'warning' validation icon width */
            /* ('required' case, ignore customValidity for simplicity) */
            padding-inline-end: calc(
                var(--button-padding-inline-end) + var(--button-padding-inline-start) + var(--button-icon-width) -
                    var(--option-padding-inline-start)
            );
        }
        &.opened .option {
            padding-inline-end: var(--option-padding-inline-start);
        }

        /* feedback state */
        &:not(.valid):not(.opened) {
            & button {
                border: var(--border-thin) solid var(--color-border-warning);
                padding-block: 0;
                padding-inline-end: calc(var(--button-padding-inline-end) + var(--button-icon-width));
            }

            /* error icon */
            & > :global(svg) {
                position: absolute;
                inset-inline-end: 5.5rem;
                inset-inline-start: auto;
                top: calc(50% - 1rem);
                color: var(--color-alert);
            }
        }
        &.opened > :global(svg) {
            display: none;
        }

        &.lite {
            & button {
                border-width: 0 0 var(--border-medium) 0;
            }

            &.opened .listbox-wrapper {
                top: calc(-1 * var(--border-medium) + var(--button-height));
            }

            &.expands-up.opened .listbox-wrapper {
                top: auto;
                bottom: calc(100%);
            }
        }
    }

    @media screen and (--mq-maxwidth-medium) {
        .select {
            max-width: 100%;
        }
    }
    /* Target only firefox to increase space for Hiragana symbols*/
    @media screen and (min--moz-device-pixel-ratio: 0) {
        .select {
            & button {
                &.with-ruby {
                    height: 130%;
                }
            }
        }
    }
</style>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<span
    class="select height-{height}"
    class:expands-up={dropDirection === 'up'}
    class:fullwidth
    class:opened
    class:lite
    class:required
    class:fixed-size={fixedSizeLetters}
    class:valid={firstTry || (validity && validity.overallValidity)}
    style={customCSSProperties}
    bind:this={outerElement}
    on:keydown={handleKeyDown}>
    <button
        type="button"
        class:with-ruby={isWithRubyClass}
        {disabled}
        bind:this={buttonElement}
        on:mousedown|preventDefault={toggle}
        on:mouseup={() => {
            lastDropdownOpenInfo = null;
        }}
        on:blur={() => (firstTry = false)}
        aria-expanded={opened}
        aria-haspopup="listbox"
        aria-roledescription={__('Dropdown')}
        aria-label={selectedLabelWithoutTags}
        title={selectedLabelWithoutTags}
        on:focus={() => {
            focus = true;
        }}
        on:blur={() => {
            focus = false;
        }}>
        <span class:dropdown-placeholder={!hasSelection}>{@html selectedLabel}</span>
        <Icon name={lite ? 'caret-down-12' : 'caret-down-16'} ariaHidden={true} />
    </button>
    <span
        class="listbox-wrapper"
        class:opened
        class:has-top-shadow={!fullyScrolledUp}
        class:has-bottom-shadow={!fullyScrolledDown}
        aria-hidden={opened ? void 0 : 'true'}>
        <span
            class="listbox"
            on:blur={close}
            bind:this={listElement}
            tabindex="-1"
            role="listbox"
            aria-activedescendant={focusedIndex > -1 ? optionsArray[focusedIndex].id : ''}
            aria-roledescription={__('dropdown list')}>
            <span
                class="scroll-child"
                use:visibilityObserver={listElement}
                on:isVisible={e => (fullyScrolledUp = e.detail)} />
                {#each optionsArray as option, i}
                    <!-- svelte-ignore a11y-interactive-supports-focus -->
                    <span
                        class="option"
                        class:selected={i === index}
                        class:blank={option.key === null}
                        class:focus-visible={!selectFocused && keyPressed && focusedIndex === i}
                        data-focus-visible-added={!selectFocused && keyPressed && focusedIndex === i ? '' : void 0}
                        on:mouseup={e => handleClick(i, e)}
                        on:mousedown={() => {
                            lastDropdownOpenInfo = null;
                        }}
                        id={option.id}
                        role="option"
                        aria-selected={i === index}>
                        {@html option.label}
                    </span>
                {/each}
            <span
                class="scroll-child"
                use:visibilityObserver={listElement}
                on:isVisible={e => (fullyScrolledDown = e.detail)} />
        </span>
    </span>
    <input type="hidden" data-type="select" {value} />
    {#if !firstTry && !validity.overallValidity}
        <Icon name="warning-16" ariaHidden={true} />
    {/if}
    {#if feedback && (focus || listHasFocus) && !disabled}
        <svelte:component
            this={allFeedbacks[feedback]}
            {validity}
            {...feedbackProps}
            emptyInput={!hasSelection}
            {firstTry}
            showValidFeedback={!opened}
            showInvalidFeedback={!opened}
            lang={feedbackLang} />
    {/if}
</span>
